// Print Sizing helpers (mirror of backend routes/print_sizing.py logic)
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "./constants";

const COMMON_RATIOS = {
  "1:1": 1.0, "5:4": 1.25, "4:3": 4 / 3, "7:5": 1.4, "3:2": 1.5, "16:10": 1.6,
  "5:3": 5 / 3, "16:9": 16 / 9, "2:1": 2.0, "5:2": 2.5, "3:1": 3.0,
  "4:5": 0.8, "3:4": 0.75, "5:7": 5 / 7, "2:3": 2 / 3, "10:16": 0.625,
  "3:5": 0.6, "9:16": 9 / 16, "1:2": 0.5,
};

// Compute precise decimal ratio + nearest human-readable display ratio
export function computeRatio(widthMm, heightMm) {
  const w = parseFloat(widthMm);
  const h = parseFloat(heightMm);
  if (!w || !h || h <= 0) return { decimal: null, display: null };
  const decimal = Math.round((w / h) * 10000) / 10000;
  let bestLabel = null;
  let bestDiff = null;
  for (const [label, value] of Object.entries(COMMON_RATIOS)) {
    const diff = Math.abs(value - decimal) / decimal;
    if (bestDiff === null || diff < bestDiff) {
      bestDiff = diff;
      bestLabel = label;
    }
  }
  const display = bestDiff !== null && bestDiff <= 0.04 ? bestLabel : `${decimal}:1`;
  return { decimal, display };
}

// Canonical size ordering for deriving a size range (e.g. "S-XL", "6y-14y")
const SIZE_ORDER = [
  "2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL",
  "4", "6", "8", "10", "12", "14", "16", "18", "20",
  "2y", "4y", "6y", "8y", "10y", "12y", "14y", "16y",
];

function sizeRank(size) {
  const idx = SIZE_ORDER.indexOf(size);
  return idx === -1 ? 999 : idx;
}

// Stable SKU key for the reference store (product_id preferred, falls back to name)
export function deriveGarmentSku(garment) {
  if (!garment) return "";
  return garment.product_id || garment.name || "";
}

// Derive size range from a garment's size_breakdown (sizes with qty > 0)
export function deriveSizeRange(garment) {
  const bd = garment?.size_breakdown || {};
  const sizes = Object.keys(bd).filter((s) => (parseInt(bd[s]) || 0) > 0);
  if (sizes.length === 0) return "";
  sizes.sort((a, b) => sizeRank(a) - sizeRank(b));
  if (sizes.length === 1) return sizes[0];
  return `${sizes[0]}-${sizes[sizes.length - 1]}`;
}

// Garment lines a print group is applied to (allocation placement_count > 0)
export function getAppliedGarments(artwork, garments) {
  const allocs = artwork?.allocations || [];
  return (garments || []).filter((g) => {
    const a = allocs.find((x) => x.garment_id === g.id);
    return a && (a.placement_count || 0) > 0;
  });
}

// Area (cm²) from mm dimensions, and the matching business size tier
const TIER_THRESHOLDS = [
  { max: 36, label: "XS (Up to 36cm² - Cap)" },
  { max: 100, label: "S (Up to 100cm² - Pocket)" },
  { max: 315, label: "M (Up to 315cm² - Standard Chest)" },
  { max: 625, label: "L (Up to 625cm² - Large Chest)" },
  { max: 1250, label: "XL (Up to 1250cm² - Back)" },
];

export function areaCm2(widthMm, heightMm) {
  const w = parseFloat(widthMm);
  const h = parseFloat(heightMm);
  if (!w || !h) return null;
  return Math.round(((w / 10) * (h / 10)) * 10) / 10;
}

export function suggestSizeTier(widthMm, heightMm) {
  const area = areaCm2(widthMm, heightMm);
  if (area === null) return null;
  for (const t of TIER_THRESHOLDS) {
    if (area <= t.max) return t.label;
  }
  return TIER_THRESHOLDS[TIER_THRESHOLDS.length - 1].label;
}

// ---- Global placements list (module cache + hook) ----
let _placementsCache = null;
let _placementsPromise = null;

async function fetchPlacements() {
  if (_placementsCache) return _placementsCache;
  if (!_placementsPromise) {
    _placementsPromise = axios
      .get(`${API}/sizing/placements`)
      .then((res) => {
        _placementsCache = res.data || [];
        return _placementsCache;
      })
      .catch(() => []);
  }
  return _placementsPromise;
}

export function usePlacements() {
  const [placements, setPlacements] = useState(_placementsCache || []);

  useEffect(() => {
    let mounted = true;
    fetchPlacements().then((p) => mounted && setPlacements(p));
    return () => {
      mounted = false;
    };
  }, []);

  const addPlacement = useCallback(async (name) => {
    const res = await axios.post(`${API}/sizing/placements`, { name });
    const item = res.data;
    _placementsCache = [...(_placementsCache || []), item];
    setPlacements(_placementsCache);
    return item;
  }, []);

  return { placements, addPlacement };
}

export async function lookupSizing(garmentSku, placement, sizeRange) {
  try {
    const res = await axios.get(`${API}/sizing/lookup`, {
      params: { garment_sku: garmentSku, placement, size_range: sizeRange || "" },
    });
    return res.data;
  } catch {
    return { found: false, reference: null };
  }
}

export async function confirmSizing(payload) {
  const res = await axios.post(`${API}/sizing/confirm`, payload);
  return res.data;
}

export async function uploadArtwork(file, jobId, groupId, onProgress) {
  const form = new FormData();
  form.append("file", file);
  if (jobId) form.append("job_id", jobId);
  if (groupId) form.append("group_id", groupId);
  const res = await axios.post(`${API}/sizing/artwork/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
}
